import { loadTranslateSettings } from './translateSettings';

/** Set while viewing the Spanish page so translate APIs can no-op elsewhere. */
let translateActiveForSession = false;

export function setTranslateSessionActive(active: boolean): void {
  translateActiveForSession = active;
}

export function isTranslateSessionActive(): boolean {
  return translateActiveForSession;
}

const MYMEMORY_ENDPOINT = 'https://api.mymemory.translated.net/get';

export class TranslateError extends Error {
  constructor(
    message: string,
    readonly status?: number
  ) {
    super(message);
    this.name = 'TranslateError';
  }
}

export type TranslateOptions = {
  source?: string;
  target?: string;
  /** MyMemory `de` param — raises daily limit from 5k to 50k chars (no signup). */
  contactEmail?: string;
  signal?: AbortSignal;
  /** Config test button and other non-page callers. */
  allowOutsideSpanishPage?: boolean;
};

type MyMemoryResponse = {
  responseStatus?: number;
  responseDetails?: string;
  quotaFinished?: boolean;
  responseData?: { translatedText?: string };
};

export async function translateText(
  text: string,
  options: TranslateOptions = {}
): Promise<string> {
  const phrase = text.trim();
  if (!phrase) throw new TranslateError('Nothing to translate.');
  if (!translateActiveForSession && !options.allowOutsideSpanishPage) {
    throw new TranslateError('Translation is only available on the Spanish page.');
  }

  const saved = loadTranslateSettings();
  const source = options.source ?? 'es';
  const target = options.target ?? 'en';
  const email = (options.contactEmail ?? saved.contactEmail).trim();

  const params = new URLSearchParams({
    q: phrase,
    langpair: `${source}|${target}`,
  });
  if (email) params.set('de', email);

  const init: RequestInit = { method: 'GET' };
  if (options.signal) init.signal = options.signal;

  const res = await fetch(`${MYMEMORY_ENDPOINT}?${params}`, init);

  let data: MyMemoryResponse;
  try {
    data = (await res.json()) as MyMemoryResponse;
  } catch {
    throw new TranslateError(
      res.ok ? 'Invalid response from translation service.' : `Request failed (${res.status}).`,
      res.status
    );
  }

  if (data.quotaFinished) {
    throw new TranslateError(
      'Daily translation quota reached for this device. Add your email in Tools → Config for a higher free limit, or try again tomorrow.',
      429
    );
  }

  const status = data.responseStatus ?? (res.ok ? 200 : res.status);
  if (status !== 200) {
    const detail = data.responseDetails?.trim();
    throw new TranslateError(
      detail || `Translation service error (${status}).`,
      status
    );
  }

  let translated = data.responseData?.translatedText?.trim();
  if (!translated) {
    throw new TranslateError('Unexpected translation response.');
  }

  const warningPrefix = 'MYMEMORY WARNING:';
  if (translated.toUpperCase().startsWith(warningPrefix)) {
    throw new TranslateError(
      'Daily free translation limit reached. Add your email in Tools → Config (optional, no signup) or try again tomorrow.',
      429
    );
  }

  return translated;
}
